import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

type SetupPaymentResultStatus = "success" | "error" | "pending";

interface SetupPaymentResultProps {
  status: SetupPaymentResultStatus;
  title?: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
}

const resultConfig = {
  success: {
    icon: CheckCircleOutlineRoundedIcon,
    color: "#15803d",
    bg: "rgba(34,197,94,0.08)",
    border: "rgba(34,197,94,0.22)",
    defaultTitle: "Payment completed successfully",
    defaultMessage: "Your Flow Mobile setup has started. Enjoy your assistant phone line experience.",
  },
  pending: {
    icon: CheckCircleOutlineRoundedIcon,
    color: "#C2410C",
    bg: "rgba(251,142,0,0.10)",
    border: "rgba(251,142,0,0.28)",
    defaultTitle: "Waiting for payment",
    defaultMessage: "This step stays blocked until the payment is confirmed.",
  },
  error: {
    icon: ErrorOutlineRoundedIcon,
    color: "#b91c1c",
    bg: "rgba(239,68,68,0.08)",
    border: "rgba(239,68,68,0.22)",
    defaultTitle: "Payment could not be completed",
    defaultMessage: "Something went wrong while processing your setup payment. Please try again.",
  },
};

const PENDING_STEPS = ["Payment submitted", "Confirming payment", "Activating the phone line"];

export function SetupPaymentResult({ status, title, message, actionLabel, onAction }: SetupPaymentResultProps) {
  const config = resultConfig[status];
  const Icon = config.icon;

  if (status === "pending") {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: { xs: "calc(100vh - 180px)", md: 520 },
          px: 3,
          py: 4,
          display: "grid",
          placeItems: "center",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 460 }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: "auto",
              mb: 2.5,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(251,142,0,0.12)",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: -6,
                borderRadius: "50%",
                border: "2px solid rgba(251,142,0,0.35)",
                animation: "setupWaitPulse 1.8s ease-out infinite",
              },
              "@keyframes setupWaitPulse": {
                "0%": { transform: "scale(0.92)", opacity: 0.9 },
                "100%": { transform: "scale(1.12)", opacity: 0 },
              },
            }}
          >
            <CircularProgress size={28} thickness={4.5} sx={{ color: "#FB8E00" }} />
          </Box>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: { xs: 22, sm: 26 },
              fontWeight: 950,
              color: "var(--fx-surface-text, #0F172A)",
              letterSpacing: "-0.04em",
              lineHeight: 1.1,
            }}
          >
            {title || config.defaultTitle}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              textAlign: "center",
              fontSize: 14,
              fontWeight: 600,
              color: "var(--fx-surface-textsecondary, #64748B)",
              lineHeight: 1.5,
            }}
          >
            {message || config.defaultMessage}
          </Typography>

          <Box
            sx={{
              mt: 3,
              borderRadius: "18px",
              border: "1px solid rgba(251,142,0,0.28)",
              bgcolor: "rgba(251,142,0,0.06)",
              px: 2.25,
              py: 2,
            }}
          >
            {PENDING_STEPS.map((step, index) => {
              const done = index === 0;
              const active = index === 1;
              return (
                <Box
                  key={step}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                    borderTop: index === 0 ? "none" : "1px solid rgba(251,142,0,0.16)",
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 12,
                      fontWeight: 800,
                      color: done || active ? "#FFFFFF" : "#C2410C",
                      bgcolor: done ? "#12B76A" : active ? "#FB8E00" : "transparent",
                      border: done || active ? "none" : "1.5px solid rgba(194,65,12,0.35)",
                    }}
                  >
                    {done ? "✓" : index + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: active ? 800 : 600,
                      color: active ? "var(--fx-surface-text, #0F172A)" : "var(--fx-surface-textsecondary, #64748B)",
                    }}
                  >
                    {step}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Typography
            sx={{
              mt: 2,
              textAlign: "center",
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--fx-surface-textsecondary, #64748B)",
              lineHeight: 1.45,
            }}
          >
            Keep this window open. The phone line continues as soon as the payment is confirmed.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: {
          xs: "calc(100vh - 180px)",
          md: 520,
        },
        px: 3,
        py: 4,
        display: "grid",
        placeItems: "center",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 520,
          mx: "auto",
          borderRadius: "22px",
          border: `1px solid ${config.border}`,
          bgcolor: config.bg,
          px: { xs: 2.5, sm: 4 },
          py: { xs: 3, sm: 4 },
          textAlign: "center",
          boxShadow: "0 12px 34px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            width: 58,
            height: 58,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: config.color,
            boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
          }}
        >
          <Icon sx={{ fontSize: 34 }} />
        </Box>

        <Typography
          sx={{
            fontSize: { xs: 22, sm: 26 },
            fontWeight: 950,
            color: "var(--fx-surface-text, #0F172A)",
            letterSpacing: "-0.04em",
            lineHeight: 1.1,
          }}
        >
          {title || config.defaultTitle}
        </Typography>

        <Typography
          sx={{
            mt: 1,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--fx-surface-textsecondary, #64748B)",
            lineHeight: 1.45,
          }}
        >
          {message || config.defaultMessage}
        </Typography>

        {actionLabel && onAction ? (
          <Button
            variant="contained"
            onClick={onAction}
            sx={{
              mt: 3,
              height: 42,
              px: 2.5,
              borderRadius: "10px",
              bgcolor: "#111",
              color: "#fff",
              textTransform: "none",
              fontWeight: 900,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#222",
                boxShadow: "none",
              },
            }}
          >
            {actionLabel}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
